// src/App.tsx — ticket-069 Fase 1
//
// Auth guard at the top, then a react-router tree below. The old
// massive activeTab switch moved to `src/routes/pages.tsx`; the old
// nav-hardcoding moved to `src/modules/registry.tsx`.
//
// Routes:
//   /login                — LoginForm (no Layout)
//   /                     — RootRedirect → /m/{defaultModule}/{defaultTab}
//   /m/:moduleKey         — redirects to the module's default tab
//   /m/:moduleKey/:tab    — ModuleRoute renders the page inside <Layout>
//
// Router basename derives from `import.meta.env.BASE_URL` (Vite's base).
// Change `vite.config.ts` base and router + assets stay in lockstep.

import React, { useCallback, useMemo, useState } from 'react';
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom';

import { API_BASE_URL } from '@/config';
import { Layout } from '@/components/Layout';
import { LoginForm } from '@/components/LoginForm';
import { NotificationContainer } from '@/components/NotificationContainer';
import { BrandingProvider } from '@/context/BrandingContext';
import PwaInstall from '@/components/PwaInstall';
import { BusinessProvider } from '@/context/BusinessContext';
import { DashboardShellProvider, useDashboardShell } from '@/context/DashboardShellContext';
import { ModulesProvider } from '@/context/ModulesContext';
import { PermissionsProvider } from '@/context/PermissionsContext';
import { RootErrorBoundary } from '@/components/RootErrorBoundary';
import { useOrgInfo } from '@/hooks/useOrgInfo';
import { useNotifications } from '@/hooks/useNotifications';

import ModuleRoute from '@/routes/ModuleRoute';
import RootRedirect from '@/routes/RootRedirect';
import type { PageProps } from '@/routes/pages';
import GoogleAdsOAuthCallback from '@/components/google_ads/GoogleAdsOAuthCallback';
import MetaOAuthCallback from '@/components/meta/MetaOAuthCallback';
import OAuthComplete from '@/components/OAuthComplete';
import { AssistantShell } from '@/components/assistant/AssistantShell';
import { OrionShell } from '@/modules/orion-shell/OrionShell';

/** Derive the router basename from Vite's `BASE_URL`.
 *
 *  `import.meta.env.BASE_URL` reflects `vite.config.ts`'s `base` at build
 *  time. We strip the trailing slash so react-router compares paths
 *  correctly (e.g. '/app' vs '/app/'). When the app is served at the
 *  site root (`base: '/'`), we return `undefined` so the router matches
 *  URLs verbatim — passing an explicit basename of '/' would still work
 *  but is redundant and has produced subtle bugs historically.
 *
 *  Reason this is derived (instead of hardcoded): Dockploy / Traefik may
 *  mount the SPA at different prefixes across environments. Linking the
 *  basename to `BASE_URL` keeps router + assets + dev-proxy in lockstep;
 *  changing `vite.config.ts` is the single source of truth. */
function resolveRouterBasename(): string | undefined {
  const raw = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '');
  return raw.length > 0 ? raw : undefined;
}

// ─────────────────────────────────────────────────────────────
// Auth layer
// ─────────────────────────────────────────────────────────────

interface AuthState {
  isAuthenticated: boolean;
  setIsAuthenticated: (v: boolean) => void;
  handleLogout: () => void;
}

function useAuthLayer(): AuthState {
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem('auth_token'),
  );

  const handleLogout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('org_id');
    localStorage.removeItem('user_roles');
    setIsAuthenticated(false);
  }, []);

  React.useEffect(() => {
    const onLogoutEvent = () => handleLogout();
    window.addEventListener('auth:logout', onLogoutEvent);
    return () => window.removeEventListener('auth:logout', onLogoutEvent);
  }, [handleLogout]);

  return { isAuthenticated, setIsAuthenticated, handleLogout };
}

// ─────────────────────────────────────────────────────────────
// Protected shell — renders Layout around any authenticated route
// ─────────────────────────────────────────────────────────────

function ProtectedShell({
  auth,
}: {
  auth: AuthState;
}) {
  // Shell por org (opt-in por DATOS, ver DashboardShellContext). Toda org sin
  // `custom_overrides.dashboard.shell = "orion"` sigue en <Layout> sin cambios.
  const { shell } = useDashboardShell();
  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (shell === 'orion') {
    return (
      <OrionShell onLogout={auth.handleLogout}>
        <Outlet />
      </OrionShell>
    );
  }
  return (
    <Layout isAuthenticated={auth.isAuthenticated} onLogout={auth.handleLogout}>
      <Outlet />
    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────
// Login shell — standalone layout without the nav
// ─────────────────────────────────────────────────────────────

function LoginShell({
  auth,
}: {
  auth: AuthState;
}) {
  if (auth.isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return (
    <Layout isAuthenticated={false} onLogout={auth.handleLogout}>
      <LoginForm onSuccess={() => auth.setIsAuthenticated(true)} />
    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────
// Assistant shell — full-screen ChatGPT-like (SIN el navbar de módulos)
// ─────────────────────────────────────────────────────────────

function AssistantShellRoute({
  auth,
  webChatHeaders,
}: {
  auth: AuthState;
  webChatHeaders: Record<string, string>;
}) {
  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <AssistantShell webChatHeaders={webChatHeaders} onLogout={auth.handleLogout} />;
}

// ─────────────────────────────────────────────────────────────
// App
// ─────────────────────────────────────────────────────────────

function App() {
  const auth = useAuthLayer();
  const { notifications, addNotification, removeNotification } = useNotifications();
  const apiKey = localStorage.getItem('mcp_api_key');
  const { orgName, orgLogo, agentPhoto, palette } = useOrgInfo(API_BASE_URL, apiKey);

  const webChatHeaders = useMemo(() => {
    const orgId = localStorage.getItem('org_id');
    return orgId ? { 'x-org-id': orgId } : {};
  }, [auth.isAuthenticated]);

  const webChatPathBuilder = useCallback(
    (id: string) => `/threads/${id}/messages`,
    [],
  );

  const pageProps: PageProps = useMemo(
    () => ({ addNotification, webChatHeaders, webChatPathBuilder }),
    [addNotification, webChatHeaders, webChatPathBuilder],
  );

  const router = useMemo(() => {
    const basename = resolveRouterBasename();
    const options = basename ? { basename } : undefined;
    return createBrowserRouter(
      [
        {
          path: '/login',
          element: <LoginShell auth={auth} />,
        },
        {
          // Interfaz tipo ChatGPT full-screen (sin el navbar de módulos).
          // chat.ucognet.pro aterriza aquí (ver RootRedirect).
          path: '/chat',
          element: <AssistantShellRoute auth={auth} webChatHeaders={pageProps.webChatHeaders} />,
        },
        {
          path: '/',
          element: <ProtectedShell auth={auth} />,
          children: [
            { index: true, element: <RootRedirect /> },
            {
              path: 'm/:moduleKey',
              element: <ModuleRoute pageProps={pageProps} />,
            },
            {
              path: 'm/:moduleKey/:tab',
              element: <ModuleRoute pageProps={pageProps} />,
            },
            {
              // Sub-path lets a page handle its own detail drill-in
              // (`/m/mkt-meta/campaigns/:subId`). The page component reads
              // :subId via `useParams` — pages.tsx stays a flat map.
              path: 'm/:moduleKey/:tab/:subId',
              element: <ModuleRoute pageProps={pageProps} />,
            },
            {
              // OAuth landing. Google redirects here with ?code&state;
              // callback component posts to /oauth/exchange with the
              // caller's JWT (hence: inside ProtectedShell).
              path: 'oauth/google-ads/callback',
              element: <GoogleAdsOAuthCallback />,
            },
            {
              // Meta OAuth landing — LEGACY pre-single-callback.
              // Sigue funcionando si Meta Dev Console aún tiene este URL
              // registrado. El nuevo flujo (single-callback) usa /oauth-complete.
              path: 'oauth/meta/callback',
              element: <MetaOAuthCallback />,
            },
            {
              // OAuth landing genérico del single-callback pattern.
              // El backend (https://staging-backend.efficientai.es/api/v1/oauth/<provider>/callback)
              // hace 302 redirect aquí post-exchange con ?provider=&status=&...
              // Cero token handling client-side — solo UI de resultado.
              path: 'oauth-complete',
              element: <OAuthComplete />,
            },
            {
              path: '*',
              element: <RootRedirect />,
            },
          ],
        },
      ],
      options,
    );
    // router is stable across re-renders; auth/pageProps are captured in
    // element closures and re-read via context hooks when they change.
  }, [auth, pageProps]);

  return (
    <RootErrorBoundary>
      <BrandingProvider value={{ orgName, orgLogo, agentPhoto, palette }}>
        <DashboardShellProvider>
        <ModulesProvider>
          <PermissionsProvider>
            <BusinessProvider>
              <RouterProvider router={router} />
              <NotificationContainer
                notifications={notifications}
                onRemove={removeNotification}
              />
              <PwaInstall />
            </BusinessProvider>
          </PermissionsProvider>
        </ModulesProvider>
        </DashboardShellProvider>
      </BrandingProvider>
    </RootErrorBoundary>
  );
}

export default App;
